// ============================================================
// EventBus.js - Central event dispatcher for decoupling systems
// Replace manual callback wiring between 40+ systems
// ============================================================

export class EventBus {
  constructor() {
    /** @type {Map<string, Set<Function>>} */
    this._listeners = new Map();
  }

  /**
   * Subscribe to an event.
   * @param {string} event - Event name
   * @param {Function} handler - Callback function
   * @returns {Function} Unsubscribe function
   */
  on(event, handler) {
    if (!this._listeners.has(event)) {
      this._listeners.set(event, new Set());
    }
    this._listeners.get(event).add(handler);

    // Return unsubscribe function
    return () => this.off(event, handler);
  }

  /**
   * Unsubscribe from an event.
   * @param {string} event
   * @param {Function} handler
   */
  off(event, handler) {
    const set = this._listeners.get(event);
    if (set) {
      set.delete(handler);
      if (set.size === 0) this._listeners.delete(event);
    }
  }

  /**
   * Subscribe to an event for one invocation only.
   * @param {string} event
   * @param {Function} handler
   * @returns {Function} Unsubscribe function
   */
  once(event, handler) {
    const wrapper = (...args) => {
      this.off(event, wrapper);
      handler(...args);
    };
    return this.on(event, wrapper);
  }

  /**
   * Emit an event to all subscribers.
   * @param {string} event - Event name
   * @param {...*} args - Arguments to pass to handlers
   */
  emit(event, ...args) {
    const set = this._listeners.get(event);
    if (!set) return;
    for (const handler of set) {
      handler(...args);
    }
  }

  /**
   * Check if an event has any subscribers.
   * @param {string} event
   * @returns {boolean}
   */
  hasListeners(event) {
    const set = this._listeners.get(event);
    return !!set && set.size > 0;
  }

  /**
   * Get the count of listeners for an event.
   * @param {string} event
   * @returns {number}
   */
  listenerCount(event) {
    const set = this._listeners.get(event);
    return set ? set.size : 0;
  }

  /**
   * Remove all listeners for a given event, or all events.
   * @param {string} [event] - If omitted, clears everything
   */
  clear(event) {
    if (event) {
      this._listeners.delete(event);
    } else {
      this._listeners.clear();
    }
  }
}

// Singleton instance — all systems share the same bus
export const eventBus = new EventBus();

// Standard event names (avoid magic strings)
export const GameEvents = {
  BLOCK_BROKEN: 'block:broken',
  BLOCK_PLACED: 'block:placed',
  MOB_KILLED: 'mob:killed',
  MOB_HIT: 'mob:hit',
  PLAYER_DAMAGED: 'player:damaged',
  PLAYER_DEATH: 'player:death',
  ITEM_PICKED_UP: 'item:pickedUp',
  XP_GAINED: 'xp:gained',
  EXPLOSION: 'explosion',
  PORTAL_ACTIVATED: 'portal:activated',
  GAME_MODE_CHANGED: 'gameMode:changed',
  DIMENSION_CHANGED: 'dimension:changed',
};
